import { initializeApp, type FirebaseOptions } from 'firebase/app'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { connectStorageEmulator, getStorage } from 'firebase/storage'

/**
 * Fail loudly at startup rather than surfacing an opaque Firebase error on the
 * first query. A missing env var here means `web/.env.local` is absent or
 * incomplete — see `.env.example`.
 */
function required(name: string): string {
  const value = import.meta.env[name] as string | undefined
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy .env.example to web/.env.local and populate it — ` +
        `run: firebase apps:sdkconfig WEB --project budgetmonkey-dd921`,
    )
  }
  return value
}

const firebaseConfig: FirebaseOptions = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
}

export const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)
export const storage = getStorage(app)

/**
 * Callables must be addressed in the region they were deployed to. The
 * receipt functions run in us-east1 to sit with the Storage bucket, not in
 * us-east4 with Firestore — see CLAUDE.md. Defaulting to us-central1, which is
 * what `getFunctions(app)` does, produces a 404 that reads like a missing
 * function rather than a wrong address.
 */
export const functions = getFunctions(app, 'us-east1')

/**
 * Emulator wiring. Guarded on DEV as well as the flag so a production build can
 * never be pointed at localhost, even if the env var leaks into a deploy.
 * Ports mirror `firebase.json`.
 */
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
  console.info('[firebase] Connected to local emulators')
}
